import { useState } from "react";
import { useParams, useRouter, createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User } from "lucide-react";
import {
  useStudent,
  useStudentAttendances,
  useStudentDailyQuizScores,
} from "@/lib/api/students/hooks/use-students";
import { AttendanceCalendar } from "./_components/-attendance-calendar";
import { DailyQuizChart } from "./_components/-daily-quiz-chart";

function StudentStatisticsPage() {
  const params = useParams({ strict: false });
  const router = useRouter();
  const studentId = params.id as string;

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  const { data: student, isLoading: isStudentLoading } = useStudent(studentId);
  const { data: attendancesData, isLoading: isAttendanceLoading } =
    useStudentAttendances(studentId, selectedYear, selectedMonth);
  const { data: dailyScores, isLoading: isDailyScoresLoading } =
    useStudentDailyQuizScores(studentId, selectedYear, selectedMonth);

  const handleMonthChange = (year: number, month: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  if (isStudentLoading) {
    return (
      <PageContainer>
        <Header title="학생 통계" />
        <div className="flex items-center justify-center h-64">
          <span className="text-muted-foreground">로딩 중...</span>
        </div>
      </PageContainer>
    );
  }

  if (!student) {
    return (
      <PageContainer>
        <Header title="학생 통계" />
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <span className="text-muted-foreground">
            학생을 찾을 수 없습니다.
          </span>
          <Button variant="outline" onClick={() => router.history.back()}>
            <ArrowLeft className="size-4 mr-2" />
            돌아가기
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header title="학생 통계" />
      <div className="space-y-6">
        {/* 뒤로가기 & 학생 정보 */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => router.history.back()}>
            <ArrowLeft className="size-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-full bg-muted flex items-center justify-center">
              <User className="size-5 text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">{student.name}</h1>
              <p className="text-sm text-muted-foreground">
                @{student.userId}
                {student.school && ` · ${student.school}`}
              </p>
            </div>
          </div>
        </div>

        {/* 출석 캘린더 */}
        <AttendanceCalendar
          year={selectedYear}
          month={selectedMonth}
          attendances={attendancesData?.attendances || []}
          onMonthChange={handleMonthChange}
          isLoading={isAttendanceLoading}
        />

        {/* 일별 퀴즈 점수 차트 */}
        <DailyQuizChart
          dailyScores={dailyScores || []}
          year={selectedYear}
          month={selectedMonth}
          isLoading={isDailyScoresLoading}
        />
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/students/$id/statistics")({
  component: StudentStatisticsPage,
});
